import React, { useState, useEffect } from 'react';
import { Star, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRow = ({ value, size = 'md', interactive = false, onChange }) => {
  const sizeCls = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-7 h-7' }[size];
  return (
    <div className="flex items-center gap-1" data-testid="star-row">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && onChange?.(n)}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
            aria-label={`${n} star`}
            data-testid={interactive ? `star-${n}` : undefined}
          >
            <Star className={`${sizeCls} ${filled ? 'fill-amber-400 text-amber-400' : 'text-neutral-300'}`} />
          </button>
        );
      })}
    </div>
  );
};

const initialsFor = (name = '') => {
  const parts = name.trim().split(/\s+/);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const ReviewsSection = ({ businessId, businessName }) => {
  const [reviews, setReviews] = useState([]);
  const [average, setAverage] = useState(null);
  const [count, setCount] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', rating: 0, comment: '' });

  useEffect(() => {
    if (!businessId) return;
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const loadReviews = async () => {
    try {
      const res = await axios.get(`${API}/businesses/${businessId}/reviews`);
      setReviews(res.data.reviews || []);
      setAverage(res.data.average_rating);
      setCount(res.data.count || 0);
    } catch (e) {
      console.error('Failed to load reviews', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Please enter your name');
    if (!form.email.trim()) return toast.error('Please enter your email');
    if (!form.rating) return toast.error('Please pick a star rating');
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        business_id: businessId,
        reviewer_name: form.name.trim(),
        reviewer_email: form.email.trim(),
        rating: form.rating,
        comment: form.comment.trim() || null,
      });
      toast.success('Check your email to confirm your review!', { duration: 6000 });
      setForm({ name: '', email: '', rating: 0, comment: '' });
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-8 border border-neutral-200" data-testid="reviews-section">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Customer Reviews
          </h2>
          {count > 0 ? (
            <div className="flex items-center gap-2 mt-2">
              <StarRow value={Math.round(average || 0)} size="sm" />
              <span className="text-sm text-neutral-700 font-semibold">{average?.toFixed(1)}</span>
              <span className="text-sm text-neutral-500">({count} review{count !== 1 ? 's' : ''})</span>
            </div>
          ) : (
            <p className="text-sm text-neutral-500 mt-1">No reviews yet — be the first!</p>
          )}
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="btn-primary text-sm flex items-center gap-2"
            data-testid="leave-review-btn"
          >
            <MessageCircle className="w-4 h-4" />
            Leave a Review
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-neutral-50 rounded-xl p-5 mb-6 border border-neutral-200" data-testid="review-form">
          <p className="text-sm text-neutral-600 mb-4">
            Share your experience with <strong>{businessName}</strong>. We'll email you to confirm before posting (this prevents spam).
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1">Your name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder='e.g. "John D." or "Jane Smith"'
                required
                maxLength={80}
                className="w-full px-3 py-2 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="review-name-input"
              />
              <p className="text-xs text-neutral-500 mt-1">First initial + last name is fine, e.g. "J. Smith"</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="w-full px-3 py-2 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="review-email-input"
              />
              <p className="text-xs text-neutral-500 mt-1">Never shown publicly. Used only to verify it's you.</p>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-semibold text-neutral-700 mb-2">Your rating *</label>
            <StarRow value={form.rating} size="lg" interactive onChange={(n) => setForm({ ...form, rating: n })} />
          </div>

          <div className="mb-4">
            <label className="block text-xs font-semibold text-neutral-700 mb-1">Comment (optional)</label>
            <textarea
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              maxLength={1500}
              rows={4}
              placeholder="What did you love? What could improve?"
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="review-comment-textarea"
            />
            <p className="text-xs text-neutral-500 mt-1">{form.comment.length}/1500</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary"
              data-testid="review-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-5 py-2.5 rounded-lg border border-neutral-300 text-neutral-700 hover:bg-neutral-100"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {reviews.length > 0 && (
        <div className="space-y-5" data-testid="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="border-b border-neutral-100 pb-5 last:border-0 last:pb-0">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold flex-shrink-0">
                  {initialsFor(r.reviewer_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <p className="font-semibold text-neutral-900">{r.reviewer_name}</p>
                    <StarRow value={r.rating} size="sm" />
                    <span className="text-xs text-neutral-500">
                      {r.approved_at ? new Date(r.approved_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                    </span>
                  </div>
                  {r.comment && (
                    <p className="text-neutral-700 mt-2 whitespace-pre-line">{r.comment}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
export { StarRow };
